import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, Position, useInternalNode, type EdgeProps, type InternalNode } from '@xyflow/react';
import { memo, type CSSProperties } from 'react';
import type { EdgeDirection } from '@hyphen-db-studio/architecture-core';
import { SERVICE_NODE_WIDTH } from '../../store/useArchitectureStore';
import { SERVICE_ICON_PATCH, SERVICE_TILE_SIZE } from './ServiceNode';

export interface FlowEdgeData {
  label?: string;
  direction: EdgeDirection;
  animated: boolean;
  speed?: number;
  highlighted?: boolean;
  /** During the guided story: 'focus' = a conversation in the current step, 'dim' = everything else. */
  tour?: 'focus' | 'dim' | null;
  [key: string]: unknown;
}

function arrowMarker(color: string): string {
  return `url(#flow-arrow-${color.replace('#', '')})`;
}

interface Anchor {
  x: number;
  y: number;
  position: Position;
}

/** Centre of the part of a node that edges attach to: the icon tile for basic nodes, the whole box otherwise. */
function anchorCenter(node: InternalNode): { x: number; y: number; half: number | null } {
  const { x, y } = node.internals.positionAbsolute;
  if (node.type === 'service') {
    return { x: x + SERVICE_NODE_WIDTH / 2, y: y + SERVICE_TILE_SIZE / 2, half: SERVICE_ICON_PATCH / 2 };
  }
  return { x: x + (node.measured.width ?? 0) / 2, y: y + (node.measured.height ?? 0) / 2, half: null };
}

/**
 * Basic-mode nodes have no fixed connection points: the edge meets the icon's border on the line towards the other
 * end, so a connector can be dropped anywhere on the icon. Returns null for group nodes, which keep their handles.
 */
function floatingAnchor(node: InternalNode | undefined, other: InternalNode | undefined): Anchor | null {
  if (!node || !other || node.type !== 'service') return null;
  const c = anchorCenter(node);
  const o = anchorCenter(other);
  const dx = o.x - c.x;
  const dy = o.y - c.y;
  if (!dx && !dy) return { x: c.x, y: c.y, position: Position.Right };
  const half = c.half ?? 0;
  const scale = half / Math.max(Math.abs(dx), Math.abs(dy));
  const horizontal = Math.abs(dx) >= Math.abs(dy);
  return {
    x: c.x + dx * scale,
    y: c.y + dy * scale,
    position: horizontal ? (dx > 0 ? Position.Right : Position.Left) : dy > 0 ? Position.Bottom : Position.Top,
  };
}

function FlowEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}: EdgeProps & { data?: FlowEdgeData }) {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const from = floatingAnchor(sourceNode, targetNode);
  const to = floatingAnchor(targetNode, sourceNode);

  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX: from?.x ?? sourceX,
    sourceY: from?.y ?? sourceY,
    sourcePosition: from?.position ?? sourcePosition,
    targetX: to?.x ?? targetX,
    targetY: to?.y ?? targetY,
    targetPosition: to?.position ?? targetPosition,
    borderRadius: 10,
  });

  const direction = data?.direction ?? 'forward';
  const animated = data?.animated ?? true;
  const speed = data?.speed ?? 1;
  const highlighted = !!data?.highlighted || !!selected;

  const focused = data?.tour === 'focus';
  const dimmed = data?.tour === 'dim';
  const strokeColor = focused ? '#f59e0b' : highlighted ? '#6366f1' : 'var(--edge-stroke, #94a3b8)';
  const markerColor = focused ? 'f59e0b' : highlighted ? '6366f1' : '94a3b8';
  const strokeWidth = focused ? 3.25 : highlighted ? 2.5 : 1.75;

  const flowStyle: CSSProperties | undefined = animated
    ? ({
        '--flow-end': direction === 'backward' ? 178 : -178,
        '--flow-duration': `${1.4 / speed}s`,
      } as CSSProperties)
    : undefined;

  return (
    <>
      <defs>
        <marker
          id={`flow-arrow-${markerColor}`}
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={`#${markerColor}`} />
        </marker>
      </defs>
      <BaseEdge
        id={id}
        path={path}
        style={{ stroke: strokeColor, strokeWidth, opacity: dimmed ? 0.1 : 1, transition: 'opacity 500ms' }}
        markerEnd={direction === 'forward' || direction === 'bidirectional' ? arrowMarker(markerColor) : undefined}
        markerStart={direction === 'backward' || direction === 'bidirectional' ? arrowMarker(markerColor) : undefined}
      />
      {(animated || focused) && !dimmed && (
        <path
          d={path}
          fill="none"
          stroke={focused ? '#fde68a' : highlighted ? '#c7d2fe' : '#bae6fd'}
          strokeWidth={highlighted ? 4 : 3}
          strokeLinecap="round"
          strokeDasharray="14 160"
          className="flow-edge-shine"
          style={flowStyle}
        />
      )}
      {data?.label && !dimmed && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan absolute rounded bg-white px-1.5 py-0.5 text-[11px] font-medium text-gray-600 shadow-sm dark:bg-gray-800 dark:text-gray-300"
            style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`, pointerEvents: 'all' }}
          >
            {data.label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export default memo(FlowEdge);
